import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Box, Typography, Paper, IconButton, Grid, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, List, ListItem,
  ListItemText, ListItemAvatar, alpha, Divider, FormControlLabel, Checkbox, Select,
  MenuItem, InputLabel, FormControl, Switch, Accordion, AccordionSummary, AccordionDetails,
  Alert, CircularProgress
} from '@mui/material';
import { WTooltip } from '../components/ui/WTooltip';
import { format, startOfWeek, addDays, subWeeks, addWeeks, parseISO, isWithinInterval, parse, isToday, isPast, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { getBrusselsDate } from '../utils/dateUtils';

const getAvailabilityStatus = (userAvailabilities, date, startTime, endTime) => {
  // Si l'utilisateur n'a pas spécifié de disponibilité pour ce jour
  const dateString = format(date, 'yyyy-MM-dd');
  const availabilitiesForDay = userAvailabilities.filter(a => a.date === dateString);
  if (availabilitiesForDay.length === 0) {
    return { status: 'not_specified', color: 'grey.300', label: 'Non spécifié' };
  }

  // Trie les disponibilités par heure de début pour un affichage logique
  availabilitiesForDay.sort((a, b) => (a.startTime || '00:00').localeCompare(b.startTime || '00:00'));

  const allSlots = availabilitiesForDay.map(a => {
    const timeRange = a.startTime === null ? 'Toute la journée' : `${a.startTime.substring(0, 5)} - ${a.endTime.substring(0, 5)}`;
    return { status: a.status, timeRange };
  });

  // Si l'utilisateur est indisponible toute la journée, on le signale directement
  const isUnavailableAllDay = availabilitiesForDay.some(a => a.startTime === null && a.status === 0);
  if (isUnavailableAllDay) {
    return { status: 'unavailable', color: alpha('#f44336', 0.3), label: 'Indisponible' };
  }

  // Création de l'intervalle de temps demandé pour la tâche
  const requestedInterval = {
    start: parse(startTime, 'HH:mm:ss', new Date()),
    end: parse(endTime, 'HH:mm:ss', new Date()),
  };

  // On cherche l'intervalle de disponibilité qui contient l'horaire de la tâche
  const coveringAvailability = availabilitiesForDay.find(av => {
    if (av.startTime === null) return true; // "Toute la journée" couvre tout
    const availabilityInterval = {
      start: parse(av.startTime, 'HH:mm:ss', new Date()),
      end: parse(av.endTime, 'HH:mm:ss', new Date()),
    };
    // Vérifie si l'intervalle de la tâche est entièrement contenu dans l'intervalle de disponibilité
    return requestedInterval.start >= availabilityInterval.start && requestedInterval.end <= availabilityInterval.end;
  });

  if (coveringAvailability) {
    switch (coveringAvailability.status) {
      case 0: // Indisponible
        return { status: 'unavailable', color: alpha('#f44336', 0.3), label: 'Indisponible' };
      case 1: // Disponible
        return { status: 'available', color: alpha('#4caf50', 0.3), label: 'Disponible' };
      case 2: // À confirmer
        return { status: 'to_confirm', color: alpha('#ff9800', 0.3), label: 'À confirmer' };
      default:
        return { status: 'not_specified', color: 'grey.300', label: 'Non spécifié' };
    }
  }

  // Si aucun intervalle ne couvre l'horaire, on considère que c'est une indisponibilité implicite
  return { status: 'unavailable_slot', color: alpha('#f44336', 0.3), label: 'Indisponible (créneau)', availableSlots: allSlots };
};

const ScheduleDialog = ({ open, onClose, onSave, users, availabilities, task, date, schedules }) => {
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('21:00');
  const [note, setNote] = useState('');
  const [requestConfirmation, setRequestConfirmation] = useState(true);

  useEffect(() => {
    if (task && date) {
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const defaultSchedule = task.DefaultSchedules.find(ds => ds.dayOfWeek === dayOfWeek);
      if (defaultSchedule) {
        setStartTime(defaultSchedule.startTime.substring(0, 5));
        setEndTime(defaultSchedule.endTime.substring(0, 5));
      } else {
        setStartTime('08:00');
        setEndTime('21:00');
      }
      setNote(''); // Reset note field
      setRequestConfirmation(true); // Reset checkbox on open
    }
  }, [task, date]);

  if (!open) return null;

  // On ne garde que les utilisateurs actifs pour l'assignation
  const activeUsers = users.filter(u => u.CompanyUser.status === 'active');

  const defaultUserIds = task.DefaultUsers?.map(u => u.id) || [];
  const defaultUsers = activeUsers.filter(u => defaultUserIds.includes(u.id));
  const otherUsers = activeUsers.filter(u => !defaultUserIds.includes(u.id));

  const renderUserList = (userList) => {
    return userList.map(user => {
      const userAvailabilities = availabilities.filter(a => a.userId === user.id);
      const availabilityStatus = getAvailabilityStatus(userAvailabilities, date, `${startTime}:00`, `${endTime}:00`);
      const isAlreadyScheduled = schedules.some(
        s => s.userId === user.id && s.taskId === task.id && s.date === format(date, 'yyyy-MM-dd')
      );
      const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '';
      const firstInitial = user.firstName ? user.firstName.charAt(0) : '';
      const lastInitial = user.lastName ? user.lastName.charAt(0) : '';
      const avatarLetters = (firstInitial || lastInitial)
        ? `${firstInitial}${lastInitial}`.toUpperCase()
        : (user.email ? user.email.charAt(0).toUpperCase() : '?');

      return (
        <ListItem
          key={user.id}
          button
          disabled={isAlreadyScheduled}
          onClick={() => !isAlreadyScheduled && onSave({ userId: user.id, taskId: task.id, date: format(date, 'yyyy-MM-dd'), startTime, endTime, note, confirmationStatus: requestConfirmation ? 'wait' : 'force' })}
          sx={{ backgroundColor: availabilityStatus.color, mb: 0.5, borderRadius: 1 }}
        >
          <ListItemAvatar sx={{ minWidth: 40 }}>
            <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>{avatarLetters}</Avatar>
          </ListItemAvatar>
          {availabilityStatus.status === 'unavailable_slot' && availabilityStatus.availableSlots.length > 0 ? (
            <WTooltip
              title={
                <Box>
                  {availabilityStatus.availableSlots.map((slot, index) => (
                    <Typography
                      key={index}
                      variant="caption"
                      display="block"
                      sx={{
                        color: "white",
                        px: 1,
                        borderRadius: 1,
                        backgroundColor: {
                          0: '#f44336', // Indisponible
                          1: '#4caf50', // Disponible
                          2: '#ff9800'  // À confirmer
                        }[slot.status] || 'text.primary'
                      }}
                    >{slot.timeRange}</Typography>
                  ))}
                </Box>
              }
            >
              <ListItemText primary={displayName} secondary={isAlreadyScheduled ? 'Déjà planifié' : availabilityStatus.label} />
            </WTooltip>
          ) : (
            <ListItemText primary={displayName} secondary={isAlreadyScheduled ? 'Déjà planifié' : availabilityStatus.label} />
          )}
        </ListItem>
      );
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ajouter un employé à la tâche "{task.name}"</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {format(date, 'eeee d MMMM yyyy', { locale: fr })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
          <TextField label="Heure de début" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Heure de fin" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>
        <TextField
          label="Note (optionnel)"
          multiline
          minRows={1}
          maxRows={4}
          value={note}
          onChange={e => setNote(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{ my: 2 }}
        />
        <Box sx={{ my: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={requestConfirmation}
                onChange={(e) => setRequestConfirmation(e.target.checked)}
              />
            }
            label="Demander une confirmation à l'employé"
          />
        </Box>
        <List dense>
          {renderUserList(defaultUsers)}
        </List>
        {otherUsers.length > 0 && (
          <Accordion sx={{ mt: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" color="text.secondary">Autres employés</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <List dense>
                {renderUserList(otherUsers)}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Annuler</Button></DialogActions>
    </Dialog>
  );
};

const EditScheduleDialog = ({ open, onClose, onUpdate, onDelete, schedule }) => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [note, setNote] = useState('');
  const [isForced, setIsForced] = useState(false);
  const [createdByName, setCreatedByName] = useState('');
  const [updatedByName, setUpdatedByName] = useState('');
  const [creationDate, setCreationDate] = useState('');
  const [updateDate, setUpdateDate] = useState('');

  useEffect(() => {
    if (schedule) {
      setStartTime(schedule.startTime.substring(0, 5));
      setEndTime(schedule.endTime.substring(0, 5));
      setNote(schedule.note || '');
      // Le switch ne gère que 'En attente' et 'Forcé'
      setIsForced(schedule.confirmationStatus === 'force');
      setCreatedByName(schedule.CreatedBy ? `${schedule.CreatedBy.firstName} ${schedule.CreatedBy.lastName}`.trim() : 'Système');
      setUpdatedByName(schedule.UpdatedBy ? `${schedule.UpdatedBy.firstName} ${schedule.UpdatedBy.lastName}`.trim() : 'Système');
      setCreationDate(format(parseISO(schedule.createdAt), 'd MMM yy à HH:mm', { locale: fr }));
      setUpdateDate(format(parseISO(schedule.updatedAt), 'd MMM yy à HH:mm', { locale: fr }));
    }
  }, [schedule]);

  if (!open || !schedule) return null;

  const handleUpdate = () => {
    // On ne peut mettre à jour le statut que s'il n'est pas final ('confirm' ou 'refuse')
    const newStatus = ['confirm', 'refuse'].includes(schedule.confirmationStatus)
      ? schedule.confirmationStatus
      : isForced ? 'force' : 'wait';

    onUpdate(schedule.id, { startTime, endTime, confirmationStatus: newStatus, note });
  };

  const handleDelete = () => {
    if (schedule.confirmationStatus === 'confirm') {
      const confirmed = window.confirm(
        "Cette affectation a déjà été confirmée par l'employé. Êtes-vous sûr de vouloir la supprimer ? Une notification sera envoyée à l'employé."
      );
      if (confirmed) {
        onDelete(schedule.id);
      }
    } else {
      // Pour les autres statuts, on peut supprimer directement ou demander une confirmation simple
      const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer cette affectation ?");
      if (confirmed) {
        onDelete(schedule.id);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Modifier l'horaire</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {(schedule.User.firstName && schedule.User.lastName) ? `${schedule.User.firstName} ${schedule.User.lastName}` : schedule.User.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {format(parseISO(schedule.date), 'eeee d MMMM yyyy', { locale: fr })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
          <TextField label="Heure de début" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Heure de fin" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Box>
        <TextField
          label="Note"
          multiline
          minRows={1}
          maxRows={4}
          value={note}
          onChange={e => setNote(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{ mt: 2 }}
        />
        <Box sx={{ my: 2 }}>
          {['confirm', 'refuse'].includes(schedule.confirmationStatus) ? (
            <Typography variant="body1">
              Statut : <strong>{schedule.confirmationStatus === 'confirm' ? 'Confirmé' : 'Refusé'}</strong>
            </Typography>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={isForced}
                  onChange={(e) => setIsForced(e.target.checked)}
                />
              }
              label={isForced ? "Pas de demande de confirmation" : "En attente de confirmation"}
            />
          )}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'text.secondary' }}>
          <WTooltip title={`Ajouté par: ${createdByName}`}>
            <Typography variant="caption">
              Créé le: {creationDate}
            </Typography>
          </WTooltip>
          <WTooltip title={`Modifié par: ${updatedByName}`}>
            <Typography variant="caption">
              Modifié le: {updateDate}
            </Typography>
          </WTooltip>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={handleDelete} color="error">Supprimer</Button>
        <Box>
          <Button onClick={onClose}>Annuler</Button>
          <Button onClick={handleUpdate} variant="contained">Enregistrer</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const DayViewDialog = ({ open, onClose, day, schedules, users, tasks }) => {
  if (!open || !day) return null;

  const dayString = format(day, 'yyyy-MM-dd');
  const schedulesForDay = schedules.filter(s => s.date === dayString);

  // Group schedules by taskId for efficient lookup
  const schedulesByTaskId = schedulesForDay.reduce((acc, schedule) => {
    if (!acc[schedule.taskId]) {
      acc[schedule.taskId] = [];
    }
    acc[schedule.taskId].push(schedule);
    return acc;
  }, {});

  // Use the sorted `tasks` array to determine the display order
  const sortedTasksWithSchedules = tasks.filter(task => schedulesByTaskId[task.id]);

  const timeToPercentage = (time) => {
    const startHour = 6;
    const totalHours = 18; // From 6:00 to 24:00
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = (hours - startHour) * 60 + minutes;
    return Math.max(0, (totalMinutes / (totalHours * 60)) * 100);
  };

  const taskColors = tasks.reduce((acc, task, index) => {
    const colors = ['#2196f3', '#f44336', '#4caf50', '#ff9800', '#9c27b0', '#795548'];
    acc[task.id] = colors[index % colors.length];
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Agenda du {format(day, 'eeee d MMMM yyyy', { locale: fr })}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', ml: '205px', mr: '15px', height: '20px' }}>
          {[6, 10, 14, 18, 22].map(hour => {
            const position = ((hour - 6) / 18) * 100;
            return (
              <Typography
                key={hour}
                variant="caption"
                color="text.secondary"
                sx={{ position: 'absolute', left: `calc(${position}%)`, transform: 'translateX(-50%)' }}
              >{hour}h</Typography>
            );
          })}
        </Box>
        {sortedTasksWithSchedules.length === 0 ? (
          <Typography>Aucun horaire planifié pour cette journée.</Typography>
        ) : (
          <List>
            {sortedTasksWithSchedules.map(task => (
              <ListItem key={task.id} divider sx={{ display: 'flex', alignItems: 'flex-start', py: 2 }}>
                <Box sx={{ width: '174px', flexShrink: 0, mt: '5px', pr: 2 }}>
                  <Typography fontWeight="bold">{task.name}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1, ml: 2, position: 'relative', background: 'linear-gradient(to right, #eee 1px, transparent 1px) 0 0 / calc(100% / 4.5) 100%' }}>
                  <Box>
                    {schedulesByTaskId[task.id].map(schedule => {
                      const start = timeToPercentage(schedule.startTime);
                      const end = timeToPercentage(schedule.endTime);
                      const user = users.find(u => u.id === schedule.userId);
                      const displayName = user ? (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email) : 'Utilisateur inconnu';
                      return (
                        <Box key={schedule.id} sx={{ position: 'relative', height: '30px', mb: '4px' }}>
                          <WTooltip
                            key={schedule.id}
                            title={`${displayName}: ${schedule.startTime.substring(0, 5)} - ${schedule.endTime.substring(0, 5)}`}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: `${start}%`,
                                width: `calc(${end - start}% - 2px)`,
                                height: '100%',
                                backgroundColor: alpha(taskColors[schedule.taskId] || '#ccc', 0.7),
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                px: 1,
                                overflow: 'hidden',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'white',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  // textOverflow: 'ellipsis',
                                }}
                              >
                                {displayName}
                              </Typography>
                            </Box>
                          </WTooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
};

const Planning = () => {
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(getBrusselsDate(), { weekStartsOn: 1 }));
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availabilities, setAvailabilities] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState({ task: null, date: null });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDayViewOpen, setIsDayViewOpen] = useState(false);
  const [selectedDayForView, setSelectedDayForView] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(new Date());

  const { activeCompany } = useContext(AuthContext);

  const fetchData = async (isBackground = false) => {
    if (!activeCompany) return;
    if (!isBackground) {
      setLoading(true);
      setError('');
    }
    try {
      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      const weekEnd = format(addDays(currentWeek, 6), 'yyyy-MM-dd');

      const [tasksRes, schedulesRes, usersRes, availabilitiesRes] = await Promise.all([
        axiosInstance.get(`/task/${activeCompany.id}`),
        axiosInstance.get(`/schedule?companyId=${activeCompany.id}&startDate=${weekStart}&endDate=${weekEnd}`),
        axiosInstance.get(`/company/${activeCompany.id}/users`),
        axiosInstance.get(`/availability/company/${activeCompany.id}?startDate=${weekStart}&endDate=${weekEnd}`),
      ]);

      setTasks(tasksRes.data);
      setSchedules(schedulesRes.data);
      setUsers(usersRes.data);
      setAvailabilities(availabilitiesRes.data);
      setLastFetchTime(new Date()); // Update last fetch time on success
    } catch (error) {
      console.error("Failed to fetch planning data", error);
      if (!isBackground) {
        setError('Erreur lors du chargement du planning.');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeCompany, currentWeek]);

  useEffect(() => {
    if (!activeCompany || loading) return;

    const interval = setInterval(async () => {
      try {
        const weekStart = format(currentWeek, 'yyyy-MM-dd');
        const weekEnd = format(addDays(currentWeek, 6), 'yyyy-MM-dd');

        const params = new URLSearchParams({
          companyId: activeCompany.id,
          lastFetchTime: lastFetchTime.toISOString(),
          startDate: weekStart,
          endDate: weekEnd,
          scheduleCount: schedules.length,
          taskCount: tasks.length,
          userCount: users.length,
          availabilityCount: availabilities.length,
        });

        const res = await axiosInstance.get(`/schedule/last-update?${params.toString()}`);

        if (res.data.hasUpdates) {
          console.log('Changes detected, refreshing data...');
          fetchData(true); // Fetch data in the background
        }
      } catch (error) {
        console.error("Failed to check for updates", error);
      }
    }, 10000); // 10000ms = 10 seconds

    return () => clearInterval(interval); // Cleanup on component unmount
  }, [activeCompany, currentWeek, lastFetchTime, loading, schedules, tasks, users, availabilities]);

  const handlePrevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));

  const handleOpenDialog = (task, date) => {
    setSelectedCell({ task, date });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedCell({ task: null, date: null });
  };

  const handleOpenEditDialog = (schedule) => {
    setSelectedSchedule(schedule);
    setIsEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setSelectedSchedule(null);
  };

  const handleOpenDayView = (day) => {
    setSelectedDayForView(day);
    setIsDayViewOpen(true);
  };

  const handleCloseDayView = () => {
    setIsDayViewOpen(false);
    setSelectedDayForView(null);
  };

  const handleSaveSchedule = async (scheduleData) => {
    try {
      const response = await axiosInstance.post('/schedule', { ...scheduleData, companyId: activeCompany.id });
      const newSchedule = response.data;
      // Find the full user object from the users state to include it in the new schedule
      const user = users.find(u => u.id === newSchedule.userId);
      if (user) {
        setSchedules([...schedules, { ...newSchedule, User: user }]);
      }
      handleCloseDialog();
    } catch (error) {
      console.error("Failed to save schedule", error);
    }
  };

  const handleUpdateSchedule = async (scheduleId, scheduleData) => {
    try {
      const response = await axiosInstance.put(`/schedule/${scheduleId}`, { ...scheduleData, companyId: activeCompany.id });
      const updatedSchedule = response.data;

      // Mettre à jour l'état local avec les données fraîches du serveur
      setSchedules(schedules.map(s => {
        if (s.id === scheduleId) {
          return updatedSchedule; // Utiliser l'objet complet renvoyé par l'API
        }
        return s;
      }));
      handleCloseEditDialog();
    } catch (error) {
      console.error("Failed to update schedule", error);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axiosInstance.delete(`/schedule/${scheduleId}`, {
        data: { companyId: activeCompany.id } // Pass companyId for security check on backend
      });
      setSchedules(schedules.filter(s => s.id !== scheduleId));
      // Close the edit dialog if it was open for the deleted schedule
      if (selectedSchedule && selectedSchedule.id === scheduleId) {
        handleCloseEditDialog();
      }
    } catch (error) {
      console.error("Failed to delete schedule", error);
    }
  };
  const statusConfig = {
    refuse: { label: 'Indisponible', color: 'error.main', bgColor: alpha('#f44336', 0.2) },
    confirm: { label: 'Disponible', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
    force: { label: 'Forcé', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
    wait: { label: 'À confirmer', color: 'warning.main', bgColor: alpha('#ff9800', 0.2) },
  };

  const weekDays = Array.from({ length: 7 }).map((_, i) => addDays(currentWeek, i));

  const renderSchedulesForCell = (taskId, date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    return schedules
      .filter(s => s.taskId === taskId && s.date === dateString)
      .map(schedule => {
        const hasNote = schedule.note && schedule.note.trim() !== '';
        const tooltipTitle = (
          <React.Fragment>
            <Typography color="inherit">{`${schedule.startTime.substring(0, 5)} - ${schedule.endTime.substring(0, 5)}`}</Typography>
            {hasNote && <Typography color="inherit" sx={{ mt: 1 }}>{schedule.note}</Typography>}
          </React.Fragment>
        );

        return (
          <WTooltip
            key={schedule.id}
            title={tooltipTitle}
          >
            {(() => {
              const displayName = (schedule.User.firstName && schedule.User.lastName) ? `${schedule.User.firstName} ${schedule.User.lastName}` : schedule.User.email;
              return (
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                      <Typography
                        variant="inherit"
                        sx={{
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                        }}
                      >
                        {displayName}
                      </Typography>
                      {hasNote && <InfoOutlinedIcon sx={{ fontSize: '0.8rem', ml: 0.5, opacity: 0.7 }} />}
                    </Box>
                  }
                  onClick={() => handleOpenEditDialog(schedule)}
                  sx={{
                    backgroundColor: statusConfig[schedule.confirmationStatus].bgColor, color: statusConfig[schedule.confirmationStatus].color, fontWeight: 'bold',
                    mb: 0.5, borderRadius: 1,
                    width: '20px',
                    minWidth: '100%',
                    fontSize: { xs: '0.6rem', md: '0.75rem' },
                    '& .MuiChip-label': {
                      display: 'block',
                      overflow: 'hidden',
                      // textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      padding: 0.5,
                      width: '100%',
                    },
                    cursor: 'pointer',
                  }}
                  size="medium" />
              );
            })()}
          </WTooltip>
        )
      });
  };

  return (
    <Container maxWidth="xl">
      {/* Header for week navigation */}
      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, mb: 2 }}>
        <IconButton onClick={handlePrevWeek}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h5" sx={{ mx: 3 }}>
          Semaine du {format(currentWeek, 'd MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton onClick={handleNextWeek}>
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Planning Grid */}
      <Box
        component={Paper}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(10px, 1.5fr) repeat(7, 1fr)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header Row */}
        <Box sx={{ borderRight: '1px solid', borderColor: 'divider', p: 1, backgroundColor: 'grey.100' }}>
          <Typography variant="h6" align="center" sx={{ fontSize: { xs: '1rem', md: '1.5rem' }, display: { xs: 'none', md: 'block' } }}>Tâches</Typography>
        </Box>
        {weekDays.map(day => (
          <Box
            key={day.toString()}
            onClick={() => handleOpenDayView(day)}
            sx={{
              borderRight: '1px solid',
              borderColor: 'divider',
              // Ajout de la bordure intérieure pour le jour actuel
              boxShadow: isToday(day) ? 'inset 0 0 0 3px #4caf50' : 'none',
              p: 1,
              textAlign: 'center',
              backgroundColor: 'grey.100',
              opacity: isPast(endOfDay(day)) ? 0.6 : 1,
              cursor: 'pointer', // Garder le curseur pour ouvrir la vue journalière
              '&:hover': { backgroundColor: 'grey.200' }
            }}>
            <Typography variant="subtitle1" sx={{ display: { xs: 'none', md: 'block' } }}>{format(day, 'eeee', { locale: fr })}</Typography>
            <Typography variant="subtitle2" sx={{ display: { xs: 'block', md: 'none' } }}>{format(day, 'eeee', { locale: fr }).substring(0, 3)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', md: '1rem' } }}>{format(day, 'd/MM')}</Typography>
          </Box>

        ))}

        {/* Task Rows */}
        {loading ? (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              <Box sx={{ gridColumn: '1 / -1', borderTop: '1px solid', borderColor: 'divider' }} />
              <Box sx={{ borderRight: '1px solid', borderColor: 'divider', p: { xs: 1, sm: 1.5, md: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                    // Applique le mode d'écriture vertical uniquement sur les écrans xs
                    writingMode: { xs: 'vertical-rl', sm: 'horizontal-tb' },
                    transform: { xs: 'rotate(180deg)', sm: 'none' },
                  }}
                >{task.name}</Typography>
              </Box>
              {weekDays.map(day => (
                <Box
                  key={`${task.id}-${day.toString()}`}
                  sx={{
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    // Ajout de la bordure intérieure pour le jour actuel
                    background: isToday(day) ? '#4caf4f1e' : 'none',
                    p: { xs: 0.5, sm: 0.7, md: 1 },
                    pb: { xs: 2, sm: 2, md: 2 },
                    minHeight: '80px',
                    position: 'relative',
                    opacity: isPast(endOfDay(day)) ? 0.6 : 1,
                  }}
                >
                  {renderSchedulesForCell(task.id, day)}
                  <IconButton
                    size="small"
                    sx={{ position: 'absolute', bottom: 4, right: 4, opacity: 0.5 }}
                    onClick={() => handleOpenDialog(task, day)}
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </React.Fragment>
          ))
        )}
      </Box>
      <ScheduleDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveSchedule}
        users={users}
        availabilities={availabilities}
        task={selectedCell.task}
        date={selectedCell.date}
        schedules={schedules}
      />
      <EditScheduleDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        onUpdate={handleUpdateSchedule}
        onDelete={handleDeleteSchedule}
        schedule={selectedSchedule}
      />
      <DayViewDialog
        open={isDayViewOpen}
        onClose={handleCloseDayView}
        day={selectedDayForView}
        schedules={schedules}
        users={users}
        tasks={tasks}
      />
    </Container>
  );
};

export default Planning;